import { TableHeaderSort, TreeNodeDataType, } from "@/UI/Navigation/TreeView/TreeInterfaces";
/**
 * Dataset title under treeNode.data where molecule-identity values (currently
 * SMILES and PubChem CID) live. Surfaced in the user-facing Data panel and
 * also used as a cache so repeated lookups of the same node skip redundant
 * work. Mirrors the convention used by the other PubChem dataset titles
 * ("Properties", "Names").
 */
export const IDENTITY_DATASET_TITLE = "Identity";
/**
 * Strips HTML tags from a string. Intended for the lightweight markup this
 * cache stores (anchors), not general sanitisation, so entities are left
 * untouched.
 *
 * @param {string} html  The string possibly containing tags.
 * @returns {string}  The string with tags removed.
 */
export function stripHtmlTags(html) {
    return html.replace(/<[^>]*>/g, "");
}
/**
 * Reads a single field from a tree node's named data table, treating that
 * table as a cache. Returns null when the table or field is absent, the stored
 * value is not a string, or the (optionally HTML-stripped) value is empty, so
 * every caller can treat null uniformly as a cache miss.
 *
 * @param {TreeNode | undefined} treeNode      The node to read from.
 * @param {string}               datasetTitle  The data table title.
 * @param {string}               field         The field within that table.
 * @param {IReadFieldOptions}    [options]     Read options (e.g. stripHtml).
 * @returns {string | null}  The cached value, or null on a miss.
 */
export function getDataTableField(treeNode, datasetTitle, field, options = {}) {
    if (!treeNode || !treeNode.data) {
        return null;
    }
    const entry = treeNode.data[datasetTitle];
    const data = entry?.data;
    const raw = data?.[field];
    if (typeof raw !== "string") {
        return null;
    }
    const value = options.stripHtml ? stripHtmlTags(raw) : raw;
    return value.trim().length > 0 ? value : null;
}
/**
 * Merges arbitrary fields into a tree node's named data table, creating the
 * data container as needed. Reads any existing table entry and spreads it back
 * in so values written by other code paths coexist with the new fields rather
 * than being clobbered. This is the single read-merge-write path shared by
 * every data-table cache writer.
 *
 * @param {TreeNode | undefined}       treeNode      The node to write to.
 * @param {string}                     datasetTitle  The data table title.
 * @param {{ [key: string]: unknown }} fields        The fields to merge in.
 */
export function mergeIntoDataTable(treeNode, datasetTitle, fields) {
    if (!treeNode) {
        return;
    }
    if (!treeNode.data) {
        treeNode.data = {};
    }
    const existing = treeNode.data[datasetTitle];
    const existingData = existing && existing.type === TreeNodeDataType.Table && existing.data
        ? existing.data
        : {};
    treeNode.data[datasetTitle] = {
        data: { ...existingData, ...fields },
        type: TreeNodeDataType.Table,
        treeNodeId: treeNode.id,
        headerSort: TableHeaderSort.None,
    };
}
/**
 * Writes a single field into a tree node's named data table. Empty values are
 * skipped, since a blank cache entry is meaningless and would read back as a
 * miss anyway. Delegates to mergeIntoDataTable so the read-merge-write logic
 * lives in one place.
 *
 * @param {TreeNode | undefined} treeNode      The node to write to.
 * @param {string}               datasetTitle  The data table title.
 * @param {string}               field         The field within that table.
 * @param {string}               value         The value to store.
 */
export function setDataTableField(treeNode, datasetTitle, field, value) {
    if (!treeNode || !value) {
        return;
    }
    mergeIntoDataTable(treeNode, datasetTitle, { [field]: value });
}
